import { analyzeTabularTransactionRows } from './analyzeTabularTransactionRows';
import { loadTabularFile, pickDefaultSheet } from './loadTabularFile';

const CSV_FAMILY_EXTENSIONS = new Set(['csv', 'tsv', 'txt']);
const SPREADSHEET_EXTENSIONS = new Set(['xlsx', 'xls']);

function getFileExtension(fileName = '') {
  const match = String(fileName).toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
}

function isModeSupportedExtension(extension, mode = 'all') {
  if (mode === 'csv') return CSV_FAMILY_EXTENSIONS.has(extension);
  return CSV_FAMILY_EXTENSIONS.has(extension) || SPREADSHEET_EXTENSIONS.has(extension);
}

function normalizeFormat(format) {
  if (!format) return null;
  if (typeof format === 'string') {
    return { key: format, label: format };
  }

  return {
    key: format.key || format.id || format.label || '',
    label: format.label || format.key || format.id || ''
  };
}

function normalizeCandidate(entry) {
  const txHash = String(entry?.txHash || '').trim();
  const toAddress = String(entry?.toAddress || '').trim();

  return {
    ...entry,
    key: entry?.key || entry?.id || `row-${entry?.rowIndex ?? 'x'}-${txHash || 'nohash'}-${toAddress || 'noaddr'}`,
    txHash,
    toAddress,
    network: String(entry?.network || '').trim() || 'Unknown',
    transferType: String(entry?.transferType || '').trim(),
    amountDisplay: entry?.amountDisplay ?? null,
    amountRaw: entry?.amountRaw ?? null,
    assetSymbol: String(entry?.assetSymbol || '').trim() || null,
    warnings: Array.isArray(entry?.warnings) ? [...entry.warnings] : [],
    lookupContext: entry?.lookupContext ? { ...entry.lookupContext } : null,
    isSelectable: Boolean(entry?.isSelectable ?? txHash),
    canLookupTxHash: Boolean(entry?.canLookupTxHash),
    fromAddressCandidate: String(entry?.fromAddressCandidate || '').trim(),
    _fromAddrCandidate: String(entry?.fromAddressCandidate || '').trim()
  };
}

function buildValidationNote(warnings) {
  const normalized = Array.isArray(warnings) ? warnings.filter(Boolean) : [];
  return normalized.join(' ');
}

export function getAcceptedTabularFileTypes(mode = 'all') {
  if (mode === 'csv') {
    return '.csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain';
  }

  return '.csv,.tsv,.txt,.xlsx,.xls,text/csv,text/tab-separated-values,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

export function isSelectableImportedCandidate(row) {
  return Boolean(String(row?.txHash || '').trim());
}

export async function parseTabularFile(file, { mode = 'all', sheetName } = {}) {
  if (!(file instanceof File)) {
    throw new Error('A file is required.');
  }

  const extension = getFileExtension(file.name);
  if (!isModeSupportedExtension(extension, mode)) {
    throw new Error(
      mode === 'csv'
        ? 'Unsupported file type. Use CSV, TSV, or TXT.'
        : 'Unsupported file type. Use CSV, TSV, TXT, XLSX, or XLS.'
    );
  }

  const parsed = await loadTabularFile(file);
  const sheets = Array.isArray(parsed?.sheets) ? parsed.sheets : [];

  let selectedSheet = null;
  if (sheetName) {
    selectedSheet = sheets.find((sheet) => sheet?.name === sheetName) || null;
  }
  if (!selectedSheet) {
    selectedSheet = pickDefaultSheet(parsed);
  }

  return {
    tabularFile: parsed,
    sheet: selectedSheet,
    rows: Array.isArray(selectedSheet?.rows) ? selectedSheet.rows : [],
    fileMeta: {
      name: parsed?.fileName || file.name || '',
      fileName: parsed?.fileName || file.name || '',
      fileSize: parsed?.fileSize ?? file.size ?? 0,
      mimeType: parsed?.mimeType || file.type || '',
      extension: parsed?.extension || extension,
      sheetName: selectedSheet?.name || '',
      sheetNames: sheets.map((sheet) => sheet?.name).filter(Boolean)
    }
  };
}

export function analyzeTabularTransactions(rows, options = {}) {
  const {
    selectedCols = {},
    columnOverrides,
    ...rest
  } = options || {};

  const effectiveOverrides = {
    ...(columnOverrides || {}),
    ...(selectedCols || {})
  };

  const result = analyzeTabularTransactionRows(rows, {
    ...rest,
    columnOverrides: effectiveOverrides
  });

  const candidates = (result.entries || []).map(normalizeCandidate);
  const selectableCount = candidates.filter((candidate) => isSelectableImportedCandidate(candidate)).length;

  return {
    ...result,
    format: normalizeFormat(result.format),
    selectedCols: { ...(result.columns || {}) },
    columns: { ...(result.columns || {}) },
    candidates,
    validationNote: buildValidationNote(result.warnings),
    summary: {
      rowCount: Array.isArray(result.rows) ? result.rows.length : 0,
      candidateCount: result.importSummary?.candidateCount || candidates.length,
      selectableCount,
      readyCount: result.importSummary?.readyCount || selectableCount,
      missingHashCount: result.importSummary?.missingHashCount || 0,
      withToAddressCount: result.importSummary?.withToAddressCount || 0,
      invalidCount: result.importSummary?.invalidCount || 0
    }
  };
}
